import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Search, BookOpen, Database, Code, Brain, Coffee, Cloud, BarChart3, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';
import { useUserCourseAssignments } from '@/hooks/useUserCourseAssignments';
import { useSupabaseAuth } from '@/hooks/auth';

const iconMap = {
  sql: Database,
  python: Code,
  java: Coffee,
  javascript: Code,
  ai: Brain,
  react: Code,
  dotnet: Code,
  aws: Cloud,
  azure: Cloud,
  gcp: Cloud,
  datascience: BarChart3,
  dataengineering: Database,
  powerbi: BarChart3,
  tableau: BarChart3,
  ml: Brain,
  mobile: Code,
  backend: Database,
  database: Database,
  security: Code,
  blockchain: Code,
  'enterprise-training': BookOpen,
  'ui-ux-design': Code,
  'graphics-programming': Code,
  'mlops': Brain,
  devops: Cloud,
  appsec: Code,
  'professional-development': BookOpen,
  infrastructure: Cloud,
  'frontend-advanced': Code,
  'testing-modern': Code,
  'web3-development': Code,
  'serverless-edge': Cloud,
  selenium: Code,
  csharp: Code,
  'rpa-tools': Code,
};

const GlobalSearch: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { assignments } = useUserCourseAssignments();
  const { hasAccess } = useSupabaseAuth();

  const getSearchableCourses = () => {
    if (hasAccess('admin')) {
      return ALL_COURSE_CATEGORIES.flatMap(category => 
        category.courses.map(course => ({
          ...course,
          categoryTitle: category.title,
          categoryId: category.id,
          icon: iconMap[category.id as keyof typeof iconMap] || BookOpen
        }))
      );
    }

    return ALL_COURSE_CATEGORIES.flatMap(category => 
      category.courses
        .filter(course => 
          assignments.some(assignment => assignment.course_id === course.id)
        )
        .map(course => ({
          ...course,
          categoryTitle: category.title,
          categoryId: category.id,
          icon: iconMap[category.id as keyof typeof iconMap] || BookOpen
        }))
    );
  };

  const searchableCourses = getSearchableCourses();
  const filteredCourses = searchableCourses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.categoryTitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleCourseSelect = (course: any) => {
    const basePath = `/${course.categoryId}-courses`;
    const coursePath = `${basePath}/${course.id}`;
    navigate(coursePath);
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="relative h-8 w-40 justify-start text-sm text-muted-foreground"
      >
        <Search className="mr-2 h-3.5 w-3.5" />
        Search courses...
        <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput 
          placeholder="Search courses, categories, or content..." 
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          <CommandEmpty>No courses found.</CommandEmpty>

          <CommandGroup heading="Quick Navigation">
            <CommandItem onSelect={() => { navigate('/search-courses'); setOpen(false); }}>
              <Search className="mr-2 h-4 w-4" />
              Advanced Search
            </CommandItem>
            <CommandItem onSelect={() => { navigate('/favorites'); setOpen(false); }}>
              <BookOpen className="mr-2 h-4 w-4" />
              My Favorites
            </CommandItem>
          </CommandGroup>

          {filteredCourses.length > 0 && (
            <CommandGroup heading="Courses">
              {filteredCourses.slice(0, 8).map((course) => (
                <CommandItem
                  key={course.id}
                  onSelect={() => handleCourseSelect(course)}
                  className="flex items-center gap-2"
                >
                  <course.icon className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="font-medium">{course.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {course.categoryTitle} • {course.level}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default GlobalSearch;